
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * EntityTimestamp
 *
 * Records the most recent update timestamp for an entity type within an external service (Payrix, Twilio, Nylas). Written by the server when a webhook arrives; broadcast to clients via Socket.IO so react-query can invalidate and refetch stale data from that service without storing a local copy of the service's data.
 */
class EntityTimestamp extends Base implements EntityType {
    entityTimestampId: uuid;
    service: string;
    entity: string;
    ts: Date;
    ids: string[];

    /**
     * Create a new EntityTimestamp, with an (optional) JSON object or EntityTimestamp class
     *
     * @param {Object|EntityTimestamp} doc - Can be either a simple JSON object or a EntityTimestamp class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof EntityTimestamp | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            entityTimestampId: genUUID(),
            service: null,
            entity: null,
            ts: null,
            ids: [],
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'entityTimestamp';
        // @ts-ignore
        this.entityTimestampId = doc._entityTimestampId || doc.entityTimestampId || doc.entitytimestampid || genUUID();
        // @ts-ignore
        this.service = doc._service || doc.service || null;
        // @ts-ignore
        this.entity = doc._entity || doc.entity || null;
        // @ts-ignore
        this.ts = DateUtil.timestampOnly(doc._ts) || DateUtil.timestampOnly(doc.ts) || null;
        // @ts-ignore
        this.ids = doc._ids || doc.ids || [];

    }
    /**
     * @returns {uuid} This is a generic pointer to the entityTimestampId across all entity types
     */
    get id(): uuid {
        return this.entityTimestampId;
    }


    /**
     * Add a id to entityTimestamp.ids
     *
     * @param {string} id - The Id to add
     * @returns string - The id with any defaulted fields set
     */
    addId(id: string) : string {
        if (!id) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.ids) this.ids = [];
        this.ids.push(id);

        return id;
    }

    /**
     * Update a id from entityTimestamp
     *
     * @param {string} id - The Id to remove
     */
    updateId(id: string) {
        if (!this.ids) this.ids = [];
        
        // Find the matching object by id.idId
        const index = this.ids.findIndex(item => item === id);
        if (index !== -1) {
            this.ids[index] = id;
        }
        else this.addId(id);
    }

    /**
     * Remove a id from entityTimestamp
     *
     * @param {string} id - The Id to remove
     */
    removeId(id: string) {
        if (!this.ids) return;
        
        // Find the matching object by id.idId
        let foundItems = this.ids?.filter(item => item === id);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.ids = this.ids.filter(item => !(item === id));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonIds() {
        this.ids = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for entityTimestamp"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.entityTimestampId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["ids"];
    }

    /**
     * Default entries are Outlaw-supplied EntityTimestamp entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): EntityTimestamp[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'entityTimestamp',
            table: 'ENTITY_TIMESTAMP',
            comment: `Records the most recent update timestamp for an entity type within an external service (Payrix, Twilio, Nylas). Written by the server when a webhook arrives; broadcast to clients via Socket.IO so react-query can invalidate and refetch stale data from that service without storing a local copy of the service's data.`,
            fields: {
                entityTimestampId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for entityTimestamp'
                },
                service: {
                    type: 'string',
                    required: true,
                    comment: 'Service name'
                },
                entity: {
                    type: 'string',
                    required: true,
                    comment: 'Entity name'
                },
                ts: {
                    type: 'timestamp',
                    required: true,
                    comment: 'Timestamp'
                },
                ids: {
                    type: 'string[]',
                    required: true,
                    comment: 'Entity IDs'
                }
            }
        });
    }

}
export default EntityTimestamp;
